import type {
  SharedRoomAttachment,
  SharedRoomCommand,
  SharedRoomInvitePreview,
  SharedRoomSnapshot,
  SharedRoomSummary,
  SharedRoomView,
} from "@rakazo/contracts";

export class RoomRequestError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "RoomRequestError";
  }
}

/** Never adds bearer tokens to a URL, storage, or a room payload. */
export class SharedRoomClient {
  private csrf: Promise<string> | undefined;
  private inviteCsrf: Promise<string> | undefined;
  constructor(private readonly fetcher: typeof fetch = (input, init) => fetch(input, init)) {}

  async session(invite = false): Promise<string> {
    // The invite endpoint also creates the HttpOnly attempt cookie. A prior normal
    // room bootstrap cannot substitute for that side effect during SPA navigation.
    if (invite) {
      if (!this.inviteCsrf) {
        this.inviteCsrf = this.read<{ csrfToken: string }>("/api/room-invites/session")
          .then((result) => result.csrfToken)
          .catch((error: unknown) => {
            this.inviteCsrf = undefined;
            this.csrf = undefined;
            throw error;
          });
        this.csrf = this.inviteCsrf;
      }
      return this.inviteCsrf;
    }
    if (!this.csrf) {
      this.csrf = this.read<{ csrfToken: string }>("/api/rooms/session")
        .then((result) => result.csrfToken)
        .catch((error: unknown) => {
          this.csrf = undefined;
          throw error;
        });
    }
    return this.csrf;
  }

  async read<T>(path: string, signal?: AbortSignal): Promise<T> {
    return this.request<T>(path, { signal });
  }

  async write<T>(path: string, body: unknown, key: string, signal?: AbortSignal): Promise<T> {
    const csrf = await this.session(path.startsWith("/api/room-invites/"));
    return this.request<T>(path, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Room-CSRF": csrf, "Idempotency-Key": key },
      body: JSON.stringify(body),
      signal,
    });
  }

  async download(path: string, body: unknown, key: string): Promise<Blob> {
    if (!/^\/api\/private-room-documents\/[A-Za-z0-9_-]+\/export$/.test(path))
      throw new Error("Invalid document endpoint");
    const csrf = await this.session();
    const response = await this.fetcher(path, {
      method: "POST",
      credentials: "include",
      cache: "no-store",
      referrerPolicy: "no-referrer",
      headers: { "Content-Type": "application/json", "X-Room-CSRF": csrf, "Idempotency-Key": key },
      body: JSON.stringify(body),
    });
    if (!response.ok)
      throw new RoomRequestError(
        response.status,
        "DOCUMENT_UNAVAILABLE",
        "Документ недоступний або доступ до його джерел відкликано.",
      );
    return response.blob();
  }

  private async request<T>(path: string, init: RequestInit): Promise<T> {
    // This client only ever talks to the trusted application's own room endpoints.
    if (
      !/^\/api\/(?:rooms(?:\/|$)|room-invites\/|room-archives\/|private-context\/room-sources|private-room-documents(?:[/?]|$))/.test(
        path,
      )
    ) {
      throw new Error("Invalid room endpoint");
    }
    const response = await this.fetcher(path, {
      ...init,
      credentials: "include",
      cache: "no-store",
      referrerPolicy: "no-referrer",
    });
    if (!response.ok) {
      const problem = (await response.json().catch(() => ({}))) as {
        code?: string;
        message?: string;
      };
      if (problem.code === "ROOM_CSRF" || problem.code === "LOGIN_ATTEMPT_REQUIRED") {
        this.csrf = undefined;
        this.inviteCsrf = undefined;
      }
      // Server errors may contain diagnostics: never echo arbitrary upstream content into the room.
      throw new RoomRequestError(
        response.status,
        problem.code ?? "ROOM_REQUEST_FAILED",
        response.status === 409
          ? "Кімната змінилася. Оновіть дані й повторіть дію."
          : response.status === 401 || response.status === 403 || response.status === 404
            ? "Доступ недоступний. Попросіть власника перевірити запрошення або членство."
            : response.status === 413
              ? "Файл перевищує дозволений розмір."
              : "Не вдалося виконати дію. Спробуйте ще раз.",
      );
    }
    return response.json() as Promise<T>;
  }

  list(signal?: AbortSignal) {
    return this.read<SharedRoomSummary[]>("/api/rooms", signal);
  }
  /** The room with its latest `limit` messages, as a chat opens at its end. */
  room(id: string, signal?: AbortSignal, limit = ROOM_PAGE) {
    return this.read<SharedRoomView>(`/api/rooms/${encodeURIComponent(id)}?limit=${limit}`, signal);
  }
  create(title: string, description: string, key: string) {
    return this.write<SharedRoomView>(
      "/api/rooms",
      { title, ...(description.trim() ? { description } : {}) },
      key,
    );
  }
  command(id: string, command: SharedRoomCommand, key: string) {
    return this.write<{ room: SharedRoomView; result?: unknown }>(
      `/api/rooms/${encodeURIComponent(id)}/commands`,
      command,
      key,
    );
  }
  invite(
    id: string,
    input: {
      type: "join" | "recovery" | "reinvite" | "archive-recovery";
      targetMembershipId?: string;
      historyFromSeq?: number;
      expiresInHours?: number;
    },
    key: string,
  ) {
    return this.write<{ token: string; preview: SharedRoomInvitePreview }>(
      `/api/rooms/${encodeURIComponent(id)}/invites`,
      input,
      key,
    );
  }
  preview(token: string, key: string) {
    return this.write<SharedRoomInvitePreview>("/api/room-invites/preview", { token }, key);
  }
  redeem(token: string, displayName: string, key: string) {
    return this.write<{ roomId: string; membershipId: string; archiveId?: string }>(
      "/api/room-invites/redeem",
      { token, displayName, idempotencyKey: key, acceptAudience: true },
      key,
    );
  }
  archive(roomId: string, id: string, signal?: AbortSignal) {
    return this.read<SharedRoomSnapshot>(
      `/api/rooms/${encodeURIComponent(roomId)}/archives/${encodeURIComponent(id)}`,
      signal,
    );
  }
  async upload(id: string, file: File, key: string, signal?: AbortSignal) {
    const csrf = await this.session();
    return this.request<SharedRoomAttachment>(`/api/rooms/${encodeURIComponent(id)}/attachments`, {
      method: "POST",
      signal,
      body: file,
      headers: {
        "Content-Type": file.type || "application/octet-stream",
        "X-Room-Filename": encodeURIComponent(file.name),
        "X-Room-CSRF": csrf,
        "Idempotency-Key": key,
        "X-Room-Audience": "room_current_and_future_members",
      },
    });
  }
}

/** Messages per page of room history; the server caps a window at 500. */
export const ROOM_PAGE = 100;

export const roomClient = new SharedRoomClient();
export function roomMutationKey(): string {
  return crypto.randomUUID();
}
export function roomAttachmentUrl(roomId: string, attachmentId: string, version: number): string {
  return `/api/rooms/${encodeURIComponent(roomId)}/attachments/${encodeURIComponent(attachmentId)}/content?version=${version}`;
}

/** Explicit, typed selection only. Text, quotes, documents and display names are never parsed. */
export function inviteSecret(fragment: string): string | null {
  const secret = fragment.replace(/^#/, "");
  return /^[A-Za-z0-9_-]{1,128}\.[A-Za-z0-9_-]{1,128}\.[A-Za-z0-9_-]{43}$/.test(secret)
    ? secret
    : null;
}
export function roomInviteUrl(origin: string, secret: string): string {
  if (!inviteSecret(`#${secret}`)) throw new Error("Invalid invite secret");
  const url = new URL("/room-invite", origin);
  url.hash = secret;
  return url.toString();
}
